import cx from 'classnames';
import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useContextSelector } from 'use-context-selector';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { optimisticIdManager } from '@trello/graphql';
import { intl } from '@trello/i18n';
import {
  useBoardId,
  useEnterpriseId,
  useWorkspaceId,
} from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import type { ReactionResponse } from '@trello/realtime-updater';
import { navigate } from '@trello/router/navigate';
import type { CardBackTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import type { CommentEntity } from 'app/src/components/ActionEntities';
import { Comment } from 'app/src/components/ActionEntities/Comment';
import { BoardMembersContext } from 'app/src/components/BoardMembersContext';
import {
  useCanCommentOnBoard,
  useCanEditBoard,
} from 'app/src/components/BoardPermissionsContext';
import { editComment, LazyEditComment } from 'app/src/components/Comments';
import { applySocketReaction } from 'app/src/components/Reactions/applySocketReaction';
import { ReactionPiles } from 'app/src/components/Reactions/ReactionPiles';
import { ActionCopyLink } from './ActionCopyLink';
import { ActionFooter } from './ActionFooter';
import { ActionTimestamp } from './ActionTimestamp';
import { useCommentMemberFragment } from './CommentMemberFragment.generated';
import { useDeleteCommentFromCardMutation } from './DeleteCommentFromCardMutation.generated';
import { LazyAttachLinksPopover } from './LazyAttachLinksPopover';
import { replyToComment } from './replyToComment';
import { useUpdateCardCommentMutation } from './UpdateCardCommentMutation.generated';
import { useActionContext } from './useActionContext';
import { useAttachableLinksInComment } from './useAttachableLinksInComment';

import * as styles from './CommentDisplay.module.less';

interface CommentDisplayProps {
  commentId: string;
  memberId: string;
  messageId: string;
  date: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: TPLAT-7303 Create more specific type
  substitutions: { [key: string]: ReactElement<any> | string };
  comment: CommentEntity;
  isHighlighted?: boolean;
  reactions: ReactionResponse[];
  showReactions?: boolean;
  isEdited?: boolean;
  appCreator?: {
    id?: string | null;
    name?: string | null;
  } | null;
  cardShortUrl?: string;
  cardId: string;
  boardName?: string;
}

const source = 'cardDetailScreen';

export const CommentDisplay: FunctionComponent<CommentDisplayProps> = ({
  commentId,
  memberId,
  messageId,
  date,
  substitutions,
  comment,
  isHighlighted,
  reactions,
  showReactions = true,
  isEdited,
  appCreator,
  cardShortUrl,
  cardId,
  boardName,
}) => {
  const { triggerRef, toggle, popoverProps, hide } = usePopover();
  const {
    triggerRef: attachTriggerRef,
    toggle: toggleAttachLink,
    popoverProps: attachPopoverProps,
  } = usePopover();
  const [removeCommentFromCard, { loading: removeCommentLoading }] =
    useDeleteCommentFromCardMutation();
  const [updateCardComment] = useUpdateCardCommentMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const enterpriseId = useEnterpriseId();
  const workspaceId = useWorkspaceId();
  const boardId = useBoardId();
  const currentMemberId = useMemberId();

  const commentLink = `${cardShortUrl}#comment-${commentId}`;

  const isCopiedCard = messageId === 'actions.action_copy_comment_from_card';
  const isPending = optimisticIdManager.isOptimisticId(commentId);
  const canComment = useCanCommentOnBoard();
  const canEditBoard = useCanEditBoard();
  const canEditComment = memberId === currentMemberId;
  const canDelete = useContextSelector(
    BoardMembersContext,
    useCallback(
      (value) => value.canDeleteCommentFromMember(memberId),
      [memberId],
    ),
  );

  const { potentialAttachmentLinks } = useAttachableLinksInComment({
    commentText: comment.text,
    isEligible: canEditBoard,
    cardId,
  });

  const { data: commenterNameData } = useCommentMemberFragment({
    from: { id: memberId },
    optimistic: true,
  });

  const { data: currentMemberNameData } = useCommentMemberFragment({
    from: { id: currentMemberId },
    optimistic: true,
  });

  useEffect(() => {
    reactions?.forEach((reaction) => {
      applySocketReaction(reaction);
    });
  }, [reactions]);

  const onDelete = useCallback(async () => {
    const taskName = 'delete-comment';

    const containers = formatContainers({
      cardId,
      boardId,
      workspaceId,
      enterpriseId,
    });

    Analytics.sendClickedButtonEvent({
      buttonName: 'deleteCommentButton',
      source,
      containers,
    });

    const traceId = Analytics.startTask({
      taskName,
      source,
      containers,
    });
    try {
      await removeCommentFromCard({
        variables: {
          idAction: commentId,
          traceId,
        },
      });
      setIsDeleted(true);
      Analytics.taskSucceeded({
        taskName,
        source,
        traceId,
        containers,
      });

      Analytics.sendTrackEvent({
        action: 'deleted',
        actionSubject: 'comment',
        source,
        attributes: {
          taskId: traceId,
        },
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName,
        source,
        traceId,
        error,
        containers,
      });
    }
    // We manually call hide here to prevent flickering of the popover content after deletion.
    hide();
  }, [
    boardId,
    cardId,
    commentId,
    enterpriseId,
    hide,
    removeCommentFromCard,
    workspaceId,
  ]);

  const onUpdate = useCallback(
    async (newComment: string) => {
      const taskName = 'edit-comment';

      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      try {
        await updateCardComment({
          variables: {
            idAction: commentId,
            text: newComment,
            traceId,
          },
        });
        Analytics.taskSucceeded({
          taskName,
          source,
          traceId,
        });

        Analytics.sendTrackEvent({
          action: 'updated',
          actionSubject: 'comment',
          source,
          containers: formatContainers({
            cardId,
            boardId,
            workspaceId,
            enterpriseId,
          }),
          attributes: {
            taskId: traceId,
            isEditorEnabled: true,
          },
        });
      } catch (error) {
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });
      }
    },
    [commentId, updateCardComment, cardId, boardId, workspaceId, enterpriseId],
  );

  const onReply = useCallback(() => {
    replyToComment({
      commentText: comment.text,
      commenterUsername: dangerouslyConvertPrivacyString(
        commenterNameData?.username,
      ),
      currentMemberUsername: dangerouslyConvertPrivacyString(
        currentMemberNameData?.username,
      ),
    });

    Analytics.sendClickedButtonEvent({
      buttonName: 'replyButton',
      source,
      containers: formatContainers({
        cardId,
        boardId,
        workspaceId,
        enterpriseId,
      }),
      attributes: {
        actionId: commentId,
      },
    });
  }, [
    boardId,
    cardId,
    comment.text,
    commentId,
    commenterNameData?.username,
    currentMemberNameData?.username,
    enterpriseId,
    workspaceId,
  ]);

  const handleAddLinkAsAttachmentClick = useCallback(() => {
    toggleAttachLink();
    Analytics.sendTrackEvent({
      action: 'attached',
      actionSubject: 'link',
      source: 'cardDetailScreen',
    });
  }, [toggleAttachLink]);

  const onCopyLink = useCallback(() => {
    navigator.clipboard.writeText(commentLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 5000);
  }, [commentLink]);

  const { location } = useActionContext();
  const handleCommentWrapperClick = useCallback(
    (e: ReactMouseEvent) => {
      if (location === 'board') {
        navigate(commentLink, { trigger: true });
      }
    },
    [location, commentLink],
  );

  if (isDeleted) {
    return null;
  }

  // We define the various actions in an array before rendering so that we can properly add • separators.
  const actions = [];
  if (canEditComment && !isCopiedCard && location === 'card') {
    actions.push(
      <a
        href="#"
        onClick={() => {
          setIsEditing(true);
          editComment(commentId);
        }}
        key="edit"
      >
        <FormattedMessage
          id="templates.action.edit"
          defaultMessage="Edit"
          description="Text on link to edit a card comment"
        />
      </a>,
    );
  }
  if (canComment && memberId !== currentMemberId && location === 'card') {
    actions.push(
      <a href="#" onClick={onReply} key="reply">
        <FormattedMessage
          id="templates.action.reply"
          defaultMessage="Reply"
          description="Text on link to reply to a card comment"
        />
      </a>,
    );
  }

  if (potentialAttachmentLinks.length > 0 && location === 'card') {
    actions.push(
      <a
        href="#"
        onClick={handleAddLinkAsAttachmentClick}
        ref={attachTriggerRef}
        key="add-link-as-attachment"
      >
        <FormattedMessage
          id="templates.action.attach link"
          defaultMessage="Add link as attachment"
          description="Text on link to delete a card comment"
        />
      </a>,
    );
  }

  if (canDelete && !isCopiedCard) {
    actions.push(
      <a href="#" onClick={toggle} ref={triggerRef} key="delete">
        <FormattedMessage
          id="templates.action.delete"
          defaultMessage="Delete"
          description="Text on link to delete a card comment"
        />
      </a>,
    );
  }

  return (
    <div
      className={cx({
        [styles.wrapper]: true,
        [styles.boardComment]: location === 'board',
      })}
      data-testid={getTestId<CardBackTestIds>('card-back-action-container')}
    >
      <div>
        {isCopiedCard ? (
          <span className={styles.copiedCommentMessageWrapper}>
            <FormattedMessage
              id={messageId}
              defaultMessage="Copied comment"
              description="Description of a copied comment on a card back"
              // Since the comment shows in a box below the header, we actually don't want it as part of the translated content
              values={{ ...substitutions, comment: '' }}
            />
          </span>
        ) : (
          <span className={styles.commentMessageWrapper}>
            <FormattedMessage
              id={messageId}
              defaultMessage="Comment"
              description="Header for comment activity"
              values={{ ...substitutions }}
            />
          </span>
        )}
        {!isPending && (
          <ActionTimestamp
            date={date}
            href={`#comment-${commentId}`}
            onClickCallback={onCopyLink}
            isHighlighted={isHighlighted}
            inline
            appCreator={appCreator}
          />
        )}
        {isEdited && (
          <span className={styles.editedMessage}>
            {' '}
            <FormattedMessage
              id="templates.action.edited"
              defaultMessage="(edited)"
              description="Label indicating that the comment has been edited at some point"
            />
          </span>
        )}
        {!isPending && location === 'card' && (
          <div className={styles.copyLinkWrapper}>
            <ActionCopyLink
              isCopied={isCopied}
              onCopyLink={onCopyLink}
              type="comment"
            />
          </div>
        )}
      </div>
      {isEditing ? (
        <LazyEditComment
          boardId={boardId}
          enterpriseId={enterpriseId}
          workspaceId={workspaceId || ''}
          cardId={cardId}
          commentId={commentId}
          getComment={() => comment.text}
          saveEdit={(newComment) => {
            onUpdate(newComment);
            setIsEditing(false);
          }}
          cancelEdit={() => setIsEditing(false)}
        />
      ) : (
        <>
          <div
            className={styles.commentWrapper}
            onClick={handleCommentWrapperClick}
            role="button"
          >
            <Comment
              {...comment}
              isFullWidth
              naturalLinkStyling
              boardId={boardId}
              cardId={cardId}
              commentId={commentId}
              workspaceId={workspaceId ?? ''}
            />
          </div>
          <div className={styles.footerWrapper}>
            {isPending ? (
              <>
                <Spinner small />
                <FormattedMessage
                  id="alerts.sending"
                  defaultMessage="Sending…"
                  description="Label indicating that the comment is pending"
                />
              </>
            ) : (
              <>
                {!isCopiedCard && showReactions && (
                  <div
                    className={styles.reactionPiles}
                    aria-label={intl.formatMessage({
                      id: 'templates.reactions.add-reaction',
                      defaultMessage: 'Add reaction',
                      description: 'Aria label for add reaction button',
                    })}
                  >
                    <ReactionPiles
                      idAction={commentId}
                      canReact={canComment}
                      isCardBack
                    />
                  </div>
                )}
                {actions.map((action, index) => (
                  <Fragment key={action.key}>
                    {action}
                    {/* Add the • separator after every action besides the last */}
                    {index + 1 < actions.length && <div>•</div>}
                  </Fragment>
                ))}
              </>
            )}
          </div>
        </>
      )}
      <Popover
        {...popoverProps}
        title={
          <FormattedMessage
            id="confirm.delete comment.title"
            defaultMessage="Delete comment?"
            description="Title of the modal confirming you wish to delete a comment from a card."
          />
        }
      >
        {/*
         * A better experience than a spinner would be using optimistic updates - while possible, the state of the
         * cache for cardActions makes this not very straightforward. See filteredActionsPatching.ts to understand why.
         */}
        {removeCommentLoading ? (
          <Spinner centered />
        ) : (
          <>
            <FormattedMessage
              id="confirm.delete comment.text"
              defaultMessage="Deleting a comment is forever. There is no undo."
              description="Text of the modal confirming you wish to delete a comment from a card."
            />
            <Button
              appearance="danger"
              shouldFitContainer
              className={styles.deleteButton}
              onClick={onDelete}
            >
              <FormattedMessage
                id="confirm.delete comment.confirm"
                defaultMessage="Delete comment"
                description="Button of the modal confirming you wish to delete a comment from a card."
              />
            </Button>
          </>
        )}
      </Popover>
      {attachPopoverProps.isVisible && (
        <LazyAttachLinksPopover
          popoverProps={attachPopoverProps}
          commentText={comment.text}
          cardId={cardId}
        />
      )}
      {location === 'member' && <ActionFooter />}
    </div>
  );
};
